import React, {useEffect, useMemo, useState} from 'react';
import {Autocomplete, CircularProgress, TextField} from '@mui/material';
import {debounce} from '@mui/material/utils';
import PropTypes from 'prop-types';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Checkbox from '@mui/material/Checkbox';

const PAGE_SIZE = 20;

const PaginatedAutocomplete = (
    {
        fetchFn,
        fetchFnProps = {},
        getOptionLabel,
        value,
        onChange,
        textFieldProps,
        disablePortal = true
    }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [options, setOptions] = useState([]);
    const [cursorId, setCursorId] = useState(0);
    const [open, setOpen] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [fetch, {data, isFetching, isLoading}] = fetchFn();

    const debouncedFetch = useMemo(() =>
            debounce(async (params) => {
                await fetch(params);
            }, 300),
        [fetch]);

    useEffect(() => {
        if (!open) return;

        debouncedFetch({
            body: {
                searchTerms: {
                    fullName: searchTerm
                },
                cursorId,
                pageSize: PAGE_SIZE
            },
            ...fetchFnProps
        });
    }, [open, cursorId, searchTerm, debouncedFetch]);

    useEffect(() => {
        if (!open || !data || isLoading || isFetching || !data.length) return;

        const isNewPage = cursorId === 0 || data[0].id > cursorId;

        if (!isNewPage) return;

        if (cursorId === 0) {
            setOptions(data);
        } else {
            setOptions(prev => [...prev, ...data]);
        }

        setHasMore(data.length === PAGE_SIZE);
    }, [data, open, cursorId, isLoading, isFetching]);

    const handleInputChange = (_event, newValue, reason) => {
        if (reason === 'input') {
            setSearchTerm(newValue);
            setOptions([]);
            setCursorId(0);
            setHasMore(true);
        }
    };

    const handleLoadMore = () => {
        if (hasMore) {
            setCursorId(options[options.length - 1].id);
        }
    };

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            limitTags={5}
            disablePortal={disablePortal}
            open={open}
            value={value}
            inputValue={searchTerm}
            filterOptions={(x) => x}
            onChange={(event, newValue) => {
                onChange(event, newValue);
                setSearchTerm('');
            }}
            onOpen={() => setOpen(true)}
            onClose={() => {
                setOpen(false);
                setSearchTerm('');
                setCursorId(0);
                setOptions([]);
                setHasMore(true);
            }}
            options={options}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={isFetching || isLoading}
            onInputChange={handleInputChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    {...textFieldProps}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isFetching || isLoading ? <CircularProgress size={20}/> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            ListboxProps={{
                sx: {maxHeight: '350px'},
                onScroll: (event) => {
                    const node = event.currentTarget;

                    if (
                        !isFetching &&
                        hasMore &&
                        node.scrollTop + node.clientHeight >= node.scrollHeight - 50
                    ) {
                        handleLoadMore();
                    }
                },
            }}
            renderOption={(props, option, {selected}) => (
                <li {...props} key={option.id}>
                    <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small"/>}
                        checkedIcon={<CheckBoxIcon fontSize="small"/>}
                        checked={selected}
                    />
                    {getOptionLabel(option)}
                </li>
            )}
        />
    );
};

PaginatedAutocomplete.propTypes = {
    fetchFn: PropTypes.func,
    fetchFnProps: PropTypes.object,
    getOptionLabel: PropTypes.func,
    value: PropTypes.any,
    onChange: PropTypes.func,
    textFieldProps: PropTypes.object,
    disablePortal: PropTypes.bool
};

export default PaginatedAutocomplete;
